import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';

import cx from 'classnames';

import CampaignContent from 'components/CampaignContent/CampaignContent';
import CampaignContentsSection from 'components/CampaignContentsSection/CampaignContentsSection';
import FAQAccordionList from 'components/ContentRecord/components/FAQAccordionList/FAQAccordionList';
import DonationCampaignBannerSection from 'components/DonationCampaignBannerSection/DonationCampaignBannerSection';
import DonationOptionGridCommerce from 'components/DonationOptionGridCommerce/DonationOptionGridCommerce';
import DynamicHeadingLine from 'components/DynamicHeadingLine/DynamicHeadingLine';
import DonationWidgetBlock from 'components/PageBlocks/DonationWidgetBlock/DonationWidgetBlock';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import RowWithOffsetCol from 'components/RowWithOffsetCol/RowWithOffsetCol';
import { IDonationCampaign } from 'models/donationCampaign';

import s from './GiftCardDonationCampaignPage.module.scss';

interface GiftCardDonationCampaignPageProps {
  donationCampaign: IDonationCampaign;
}
const GiftCardDonationCampaignPage: React.FC<
  GiftCardDonationCampaignPageProps
> = ({ donationCampaign }) => {
  const topCampaignContent = useMemo(
    () => donationCampaign?.campaign_contents?.[0],
    [donationCampaign?.campaign_contents],
  );

  const restOfCampaignContents = useMemo(
    () => donationCampaign?.campaign_contents?.slice(1) || [],
    [donationCampaign?.campaign_contents],
  );

  return (
    <>
      <DonationCampaignBannerSection
        backgroundImageURL={donationCampaign.banner_image?.url}
        backgroundImageMobileURL={donationCampaign.mobile_banner_image?.url}
        bannerImageAlt={donationCampaign.banner_image_alt}
      >
        <Row className="justify-content-between">
          <Col
            xl={6}
            lg={5}
            className="px-5 pb-3 pt-5 px-lg-3 pb-lg-9 pr-lg-5 d-flex flex-column justify-content-center"
          >
            {donationCampaign.heading && !donationCampaign.heading_image && (
              <h1
                className={cx(
                  'heading',
                  'heading-secondary',
                  'text-white',
                  s['heading-bold'],
                )}
              >
                {donationCampaign.heading}
              </h1>
            )}
            {donationCampaign.heading_image && (
              <img
                className="w-100 mb-4 mt-0 mt-md-5 mt-lg-0 mb-0"
                src={donationCampaign.heading_image.url}
                alt={donationCampaign.heading_image_alt}
              />
            )}
            {donationCampaign.sub_heading && (
              <h2 className={cx('mb-4', 'text-white', s['sub-heading-bold'])}>
                {donationCampaign.sub_heading}
              </h2>
            )}
          </Col>
          {donationCampaign.heading_image_right?.url && (
            <Col
              xl={6}
              lg={7}
              className="d-none d-lg-flex px-5 px-sm-2 px-md-3 pb-9 pt-5"
            >
              <img
                className="d-block w-100 object-fit-contain"
                src={donationCampaign.heading_image_right.url}
                alt={donationCampaign.heading}
              />
            </Col>
          )}
        </Row>
      </DonationCampaignBannerSection>
      {topCampaignContent && (
        <SectionWithContainer
          noPadding
          className="pt-0 pb-4 pt-4 mt-0 mt-lg-n11 position-relative z-100"
        >
          <CampaignContent
            campaignContent={topCampaignContent}
            colour={donationCampaign.colour}
            layout={topCampaignContent.layout?.layout_type}
          />
        </SectionWithContainer>
      )}
      {!!donationCampaign.donation_options?.length && (
        <SectionWithContainer>
          <Row className="mb-4">
            <Col className="offset-lg-1" lg={6}>
              <h1 data-content="campaign_content_heading" className="text-left">
                What you can give
              </h1>
              <DynamicHeadingLine
                className="mb-0"
                colour={donationCampaign.colour}
              />
            </Col>
          </Row>
          <DonationOptionGridCommerce
            donationCampaignOptions={donationCampaign.donation_options}
            donationCampaignSlug={donationCampaign.slug}
            imageClassName="object-fit-cover"
          />
        </SectionWithContainer>
      )}
      {donationCampaign.content_brief && (
        <SectionWithContainer
          style={{
            backgroundColor: donationCampaign.colour,
          }}
          className={cx(s['campaign-content-brief'], 'py-5')}
        >
          <Row>
            <Col lg={6} xl={7}>
              <div
                className={cx(
                  s['campaign-secondary-text'],
                  'text-white',
                  'font-weight-bold',
                )}
                data-content="donation_campaign_content_brief"
                dangerouslySetInnerHTML={{
                  __html: donationCampaign.content_brief,
                }}
              />
            </Col>
          </Row>
        </SectionWithContainer>
      )}
      {!!restOfCampaignContents?.length && (
        <CampaignContentsSection
          campaignContents={restOfCampaignContents}
          colour={donationCampaign.colour}
        />
      )}
      {!!donationCampaign.featured_faqs?.length && (
        <SectionWithContainer>
          <Row className="mb-5">
            <Col lg={7}>
              <h2 className="heading heading-light-blue">
                Where does my money go? Can even a small donation help? Your
                questions answered.
              </h2>
            </Col>
          </Row>
          <RowWithOffsetCol>
            <FAQAccordionList faqs={donationCampaign.featured_faqs} />
          </RowWithOffsetCol>
        </SectionWithContainer>
      )}
      <DonationWidgetBlock
        donationCampaign={{ ...donationCampaign, donation_options: [] }}
        campaignBackgroundImageURL={donationCampaign.banner_image?.url}
        backdrop={false}
      />
    </>
  );
};

export default GiftCardDonationCampaignPage;
